import { FC, useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';
import { createColumnHelper, ColumnDef, Row } from '@tanstack/react-table';
import { Tag, Link as CarbonLink, InlineLoading } from '@carbon/react';
import { ChevronRight } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { LaunchLink } from 'components/launch-link';
import { duplicateNriCalculationsLoadableAtom } from './atoms';
import type { Well, DevArea, DuplicateNriRecord } from './types';
import styles from './page.module.scss';

const ALLOCATION_METHOD_LABELS: Record<
  string,
  { label: string; tagType: string }
> = {
  acreage: { label: 'Acreage', tagType: 'blue' },
  lateral_length: { label: 'Lateral Length', tagType: 'teal' },
};

const totalRecordsForWell = (well: Well): number =>
  well.devAreas.reduce((sum, da) => sum + da.records.length, 0);

const columnHelper = createColumnHelper<DuplicateNriRecord>();

export const recordColumns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: 'ID',
    size: 100,
  }),
  columnHelper.accessor((row) => row.objectName ?? row.objectId, {
    id: 'subtract',
    header: 'Subtract',
    size: 220,
    cell: (info) => {
      const row = info.row.original;
      const display = row.objectName ?? row.objectId;
      return row.salesforceHref ? (
        <LaunchLink
          href={row.salesforceHref}
          className={styles.link}
          label={`${display} — open in Salesforce in a new tab`}
        >
          {display}
        </LaunchLink>
      ) : (
        display
      );
    },
  }),
  columnHelper.accessor('unitAllocationTractId', {
    id: 'unitAllocationTractId',
    header: 'UAT ID',
    size: 100,
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('nri', {
    id: 'nri',
    header: 'NRI',
    size: 120,
    cell: (info) => {
      const val = info.getValue();
      return val !== null && val !== undefined ? formatNumber(val, 6, 6) : '--';
    },
  }),
  columnHelper.accessor('workingInterest', {
    id: 'workingInterest',
    header: 'Working Interest',
    size: 160,
    cell: (info) => {
      const val = info.getValue();
      return val !== null && val !== undefined ? formatNumber(val, 6, 6) : '--';
    },
  }),
  columnHelper.accessor('allocationMethod', {
    id: 'allocationMethod',
    header: 'Allocation Method',
    size: 170,
    cell: (info) => {
      const method = info.getValue();
      if (!method) return '--';
      const methodConfig = ALLOCATION_METHOD_LABELS[method] ?? {
        label: method,
        tagType: 'gray',
      };
      return (
        <Tag type={methodConfig.tagType as 'blue' | 'teal' | 'gray'} size="sm">
          {methodConfig.label}
        </Tag>
      );
    },
  }),
  columnHelper.accessor('updatedAt', {
    id: 'updatedAt',
    header: 'Updated At',
    size: 240,
    meta: { type: 'datetime' },
  }),
];

const wellColumnHelper = createColumnHelper<Well>();

export const wellColumns = [
  wellColumnHelper.accessor('wellUWI', {
    id: 'wellUWI',
    header: 'UWI',
    size: 120,
    cell: (info) => {
      const well = info.row.original;
      return (
        <LaunchLink
          to={well.wellHref}
          className={styles.link}
          label={`${well.wellUWI} — open well in a new tab`}
        >
          {well.wellUWI}
        </LaunchLink>
      );
    },
  }),
  wellColumnHelper.accessor('currentUnitName', {
    id: 'currentUnitName',
    header: 'Current Unit',
    size: 180,
    cell: (info) => {
      const well = info.row.original;
      if (!well.currentUnitName) return '--';
      return well.currentUnitHref ? (
        <LaunchLink
          to={well.currentUnitHref}
          className={styles.link}
          label={`${well.currentUnitName} — open unit in a new tab`}
        >
          {well.currentUnitName}
        </LaunchLink>
      ) : (
        well.currentUnitName
      );
    },
  }),
  wellColumnHelper.accessor((row) => row.devAreas.length, {
    id: 'unitCount',
    header: 'Duplicate Calcs: Unit Count',
    size: 230,
    meta: { type: 'integer' },
  }),
  wellColumnHelper.accessor((row) => totalRecordsForWell(row), {
    id: 'nriDuplicateCount',
    header: 'Duplicate Calcs: NRI Duplicate Count',
    size: 310,
    meta: { type: 'integer' },
  }),
  wellColumnHelper.accessor(
    (row) =>
      row.devAreas
        .flatMap((da) => [
          da.unitName,
          String(da.unitTractId),
          ...da.records.flatMap((r) => [
            String(r.id),
            r.objectName,
            r.objectId,
            r.allocationMethod,
            r.unitAllocationTractId !== null
              ? String(r.unitAllocationTractId)
              : null,
          ]),
        ])
        .filter(Boolean)
        .join(' '),
    {
      id: '_nestedSearch',
      header: '',
      size: 0,
      enableSorting: false,
    }
  ),
];

const DevAreaSection: FC<{ devArea: DevArea }> = ({ devArea }) => {
  const [isOpen, setIsOpen] = useState(false);
  const recordCount = devArea.records.length;

  return (
    <div className={styles.devAreaSection}>
      <button
        type="button"
        className={styles.devAreaHeading}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <ChevronRight
          className={`${styles.devAreaChevron} ${
            isOpen ? styles.devAreaChevronOpen : ''
          }`}
        />
        <span className={styles.accordionTitle}>
          {devArea.unitHref ? (
            <CarbonLink
              href={devArea.unitHref}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {devArea.unitName ?? devArea.unitTractId}
            </CarbonLink>
          ) : (
            (devArea.unitName ?? devArea.unitTractId)
          )}
          <span className={styles.accordionMeta}>
            {recordCount} {recordCount === 1 ? 'record' : 'records'}
          </span>
        </span>
      </button>
      {isOpen && (
        <div className={styles.devAreaContent}>
          <DataTable
            data={devArea.records}
            columns={recordColumns as ColumnDef<DuplicateNriRecord>[]}
            getRowId={(row) => String(row.id)}
            persistenceKey={`duplicate-nri-records-${devArea.unitTractId}`}
            config={{
              sorting: false,
              globalSearch: false,
              columnFilters: false,
              columnResizing: false,
              columnReordering: false,
              columnPinning: false,
              columnVisibility: false,
              fitContent: true,
            }}
          />
        </div>
      )}
    </div>
  );
};

export const DuplicateNriCalculationsPage: FC = () => {
  const loadable = useAtomValue(duplicateNriCalculationsLoadableAtom);

  const wells =
    loadable.state === 'hasData' ? (loadable.data?.wells ?? []) : [];
  const stats = loadable.state === 'hasData' ? loadable.data?.stats : null;
  const isLoading = loadable.state === 'loading';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error
        : new Error('Failed to fetch data')
      : null;

  // TODO: Deep search highlight (globalSearch state, getWellExpanderClassName,
  // onGlobalSearchChange, getExpanderClassName, and the pulse CSS) should
  // eventually be pulled up into DataTable as a built-in config option
  // (e.g. config.highlightNestedMatches) so any page with expandable rows
  // and hidden searchable columns gets this behavior automatically.
  const [globalSearch, setGlobalSearch] = useState('');

  const getWellExpanderClassName = useCallback(
    (row: Row<Well>) => {
      if (!globalSearch) return undefined;
      const term = globalSearch.toLowerCase();
      const hasNestedMatch = row.original.devAreas.some(
        (da) =>
          da.unitName?.toLowerCase().includes(term) ||
          String(da.unitTractId).includes(term) ||
          da.records.some(
            (r) =>
              String(r.id).includes(term) ||
              r.objectName?.toLowerCase().includes(term) ||
              r.objectId?.toLowerCase().includes(term) ||
              r.allocationMethod?.toLowerCase().includes(term) ||
              (r.unitAllocationTractId !== null &&
                String(r.unitAllocationTractId).includes(term))
          )
      );
      return hasNestedMatch ? styles.expanderHighlight : undefined;
    },
    [globalSearch]
  );

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Duplicate NRI Calculations"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          All NRI records that violate one of two data integrity rules:
          <br />
          (1) A well/subtract pair is mapped through multiple dev areas
          (possible stale records left behind after a well reassignment) or
          <br />
          (2) A well/subtract/dev-area combination has multiple records but
          fewer distinct UAT IDs than records (possible pre Allocation Manager
          NULL UAT stale records. A single record with a NULL UAT is not
          flagged)
          <br />
          <br />
          Both current and stale records are shown so you can identify which to
          clean up.
        </div>
        <div className={styles.expectedState}>
          Expected: Empty - no duplicate NRI calculations should exist
        </div>
      </div>

      {stats && (
        <StatsBar
          stats={[
            {
              label: 'Total Records',
              value: stats.totalRecords,
              tagType: 'gray',
            },
            {
              label: 'Duplicate Groups',
              value: stats.duplicateGroups,
              tagType: 'red',
            },
            { label: 'Wells', value: stats.wells, tagType: 'blue' },
            { label: 'Subtracts', value: stats.subtracts, tagType: 'purple' },
          ]}
        />
      )}

      {isLoading && <InlineLoading description="Loading duplicates..." />}

      {error && (
        <div className={styles.errorMessage}>
          Error loading data: {error.message}
        </div>
      )}

      {!isLoading && !error && (
        <div className={styles.wellTableWrapper}>
          <DataTable
            data={wells}
            columns={wellColumns as ColumnDef<Well>[]}
            getRowId={(row) => row.wellUWI}
            persistenceKey="duplicate-nri-wells"
            initialTableState={{
              columnVisibility: { _nestedSearch: false },
            }}
            getExpanderClassName={getWellExpanderClassName}
            renderRowSubComponent={(row: Row<Well>) => (
              <div className={styles.devAreaList}>
                {row.original.devAreas.map((da) => (
                  <DevAreaSection key={da.unitTractId} devArea={da} />
                ))}
              </div>
            )}
            onGlobalSearchChange={(s) => setGlobalSearch(s.globalFilter)}
            config={{
              sorting: true,
              globalSearch: true,
              columnFilters: true,
              columnResizing: false,
              columnReordering: true,
              columnPinning: false,
              columnVisibility: false,
              fitContent: true,
            }}
          />
        </div>
      )}
    </>
  );
};
